import { ReactElement } from "react";

import BulkRecordsTable from "./bulk-records-table";
import BulkSummary from "./bulk-summary";
import { BulkResult, flattenExcluded } from "../types";

interface ResultStepProps {
  result: BulkResult;
  busy: boolean;
  executing: boolean;
  onExecute: () => void;
}

/**
 * Step 3 — what the run found, and the execute action.
 *
 * After executing, the counters and tables shown are the *execute* response,
 * not the earlier validate preview: execute revalidates transactionally, so
 * it is the only accurate account of what was created.
 */
const ResultStep: React.FC<ResultStepProps> = ({
  result,
  busy,
  executing,
  onExecute,
}): ReactElement => {
  const executed = result.mode === "execute";
  const valid = result.counts.valid ?? 0;

  return (
    <div className="panel">
      <header>
        <h4>
          <span className="step">3</span>
          {executed ? "Execution result" : "Validation result"}
        </h4>
      </header>

      <div className="body">
        <BulkSummary counts={result.counts} mode={result.mode} />

        {executed ? (
          <div className="notice is-ok">
            <p className="title">
              {(result.counts.inserted ?? 0).toLocaleString("en-US")} record(s)
              created as Pending
            </p>
            <p>
              No notifications were sent and no documents were generated. The
              new records appear in the Portfolio agreements list.
            </p>
          </div>
        ) : (
          <div className="notice is-info">
            <p className="title">Nothing has been created yet</p>
            <p>
              Executing revalidates every row inside a transaction and inserts
              only what is still valid at that moment, so the outcome can
              differ from this preview.
            </p>
          </div>
        )}

        <BulkRecordsTable
          title={executed ? "Records submitted" : "Valid records"}
          tone="ok"
          records={result.validRecords}
          emptyMessage="No valid records in this file."
          exportFileName={executed ? "submitted-records" : "valid-records"}
        />

        <BulkRecordsTable
          title="Excluded records"
          tone="bad"
          records={flattenExcluded(result.excludedRecords)}
          showReasons
          emptyMessage="Nothing was excluded."
          exportFileName="excluded-records"
        />
      </div>

      {!executed && (
        <div className="actions">
          <span className="hint">
            Executing creates Pending records. It cannot be undone from this
            screen.
          </span>
          <button
            type="button"
            className="btn execute"
            disabled={busy || valid === 0}
            onClick={onExecute}
          >
            {executing
              ? "Executing…"
              : `Execute ${valid.toLocaleString("en-US")} record(s)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ResultStep;
